import { DataService } from '../../utils/dataService';
import { HistoricoEntry } from './trackingUtils';

export interface GuideData {
  estado: string;
  dsfechaentrega: string | null;
  dsfecha: string;
  rutave: string;
  rutadigitalizada?: string;
  historicos: HistoricoEntry[];
}

export interface GuideStatusResponse {
  success: boolean;
  data:
  | {
    aveonline: {
      guide: GuideData[];
    };
  }
  | CompactGuideData[];
}

export interface CompactGuideData {
  urlAve: string;
  day: string;
  status: string;
  urlGuide: string;
}

export interface NormalizedGuideData {
  status: string;
  estimatedDeliveryDate: string;
  date: string;
  urlAve: string;
  urlGuide: string;
  historicos: HistoricoEntry[];
}

export interface NormalizedGuideStatusResponse {
  success: boolean;
  guide: NormalizedGuideData | null;
}

/**
 * Mock scenario controlled by REACT_APP_TRACKING_MOCK_STATUS or ?mock_status= query param:
 *   "confirmado"  -> step 1 completed
 *   "recogido"    -> steps 1-2 completed
 *   "en_camino"   -> steps 1-4 completed (default)
 *   "entregado"   -> all 7 steps completed
 *   "error"       -> simulates API error (success: false)
 */
const MOCK_HISTORICOS_ALL: HistoricoEntry[] = [
  { estado: 'Confirmado', fechamostrar: '12/11/2025 16:30', descripcion: 'Pedido confirmado', aclaracion: '', novedad: 0 },
  { estado: 'Recogido', fechamostrar: '14/11/2025 18:30', descripcion: 'Pedido recogido', aclaracion: '', novedad: 0 },
  { estado: 'En bodega origen', fechamostrar: '18/11/2025 08:00', descripcion: 'En bodega de transportadora (Origen)', aclaracion: '', novedad: 0 },
  { estado: 'En tránsito', fechamostrar: '20/11/2025 19:00', descripcion: 'En camino a destino', aclaracion: '', novedad: 0 },
  { estado: 'En bodega destino', fechamostrar: '24/11/2025 05:00', descripcion: 'En bodega de transportadora (Destino)', aclaracion: '', novedad: 0 },
  { estado: 'En reparto', fechamostrar: '25/11/2025 13:00', descripcion: 'En reparto', aclaracion: '', novedad: 0 },
  { estado: 'Entregado', fechamostrar: '26/11/2025 15:00', descripcion: 'Pedido entregado', aclaracion: '', novedad: 0 },
];

const MOCK_STATUS_TO_COUNT: Record<string, number> = {
  confirmado: 1,
  recogido: 2,
  en_origen: 3,
  en_camino: 4,
  en_destino: 5,
  en_reparto: 6,
  entregado: 7,
};

function getMockStatus(): string {
  const params = new URLSearchParams(window.location.search);
  const queryStatus = params.get('mock_status');
  return queryStatus || process.env.REACT_APP_TRACKING_MOCK_STATUS || 'en_camino';
}

function buildMockResponse(): NormalizedGuideStatusResponse {
  const status = getMockStatus();

  if (status === 'error') {
    return { success: false, guide: null };
  }

  const completedCount = MOCK_STATUS_TO_COUNT[status] ?? 4;
  const historicos = MOCK_HISTORICOS_ALL.slice(0, completedCount);
  const lastHistorico = historicos[historicos.length - 1];

  return {
    success: true,
    guide: {
      status: lastHistorico.estado,
      estimatedDeliveryDate: completedCount === 7 ? '26/11/2025' : '12/11/2025',
      date: '12/11/2025',
      urlAve: 'https://aveonline.co/mock-label',
      urlGuide: '',
      historicos,
    },
  };
}

function isMockEnabled(): boolean {
  return process.env.REACT_APP_TRACKING_MOCK === 'true';
}

async function fetchGuideStatusMock(): Promise<NormalizedGuideStatusResponse> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(buildMockResponse());
    }, 800);
  });
}

export function normalizeGuideStatusResponse(
  response: GuideStatusResponse
): NormalizedGuideStatusResponse {
  if (!response.success) {
    return { success: false, guide: null };
  }

  if (Array.isArray(response.data)) {
    const guide = response.data[0];

    return {
      success: Boolean(guide),
      guide: guide
        ? {
          status: guide.status,
          estimatedDeliveryDate: '',
          date: guide.day,
          urlAve: guide.urlAve,
          urlGuide: guide.urlGuide,
          historicos: [],
        }
        : null,
    };
  }

  const guide = response.data?.aveonline?.guide?.[0];

  return {
    success: Boolean(guide),
    guide: guide
      ? {
        status: guide.estado,
        estimatedDeliveryDate: guide.dsfechaentrega ?? '',
        date: guide.dsfecha,
        urlAve: guide.rutave,
        urlGuide: guide.rutadigitalizada ?? '',
        historicos: guide.historicos ?? [],
      }
      : null,
  };
}

export async function fetchGuideStatus(
  shoppingCartId: string,
  url: string
): Promise<NormalizedGuideStatusResponse> {
  if (isMockEnabled()) {
    return fetchGuideStatusMock();
  }

  const dataService = new DataService();
  const body = {
    source: 'shops-integraciones',
    url,
    shoppingCartId,
  };
  const response = await dataService.post('/applications/aveonline/guide', body);
  return normalizeGuideStatusResponse(response.data as GuideStatusResponse);
}
